import { useState, useEffect, useMemo } from 'react'
import { X, Sparkles, BookOpen, Film, Loader2, FileText } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'
import { dedupAiKeywordAgainstLlm } from './dedupMoments'

// VideoReelComposer — single-video reel builder fed by Mux Robots output.
//
// Pulls the video's key moments (Find Key Moments) and chapters (Generate
// Chapters), interleaves them by start_ms, and lets the editor multi-select
// rows for a reel. Key moments are checked by default (they're scored as
// "compelling moments" by Mux); chapters are unchecked by default (they're
// structural markers — useful as anchor points but not always reel material).
//
// Posts straight to /api/highlights/reels using the existing reel pipeline.
// Default mode is free preview (instant-clipping chain); editor can flip to
// encoded (paid Mux stitch) for a shareable / downloadable asset.

function fmtTime(ms) {
  const sec = Math.floor((ms || 0) / 1000)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

function scoreColor(score) {
  if (score == null) return '#888'
  if (score >= 0.85) return '#22c55e'
  if (score >= 0.7)  return '#a78bfa'
  if (score >= 0.5)  return '#3b82f6'
  return '#888'
}

// Keys for the per-row selection map. Combine type + start_ms so a chapter
// at 00:00 and a key moment at 00:00 don't collide.
const itemKey = (it) => `${it._type}:${it.start_ms}`

// Cap for chapter clip duration. Chapters are conceptual sections (period,
// drive, halftime) and can be many minutes long. A reel that included the
// full chapter span would balloon. 60s gives the viewer meaningful context
// while keeping the per-segment cost in check.
const CHAPTER_MAX_WINDOW_MS = 60000

export default function VideoReelComposer({ tbtv_id, title, onClose, onCreated }) {
  const api = useHighlightsApi()
  const [moments, setMoments] = useState(null)
  const [chapters, setChapters] = useState(null)
  const [summary, setSummary] = useState(null)
  const [err, setErr] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  // Title seeds with the source video name; updates live as the editor
  // changes the selection so it reflects "what's in the reel" by default.
  // The editor can type over it at any point and we won't overwrite their
  // edit (tracked via `titleEdited`).
  const [reelTitle, setReelTitle] = useState(`${title || tbtv_id} — highlights`)
  const [titleEdited, setTitleEdited] = useState(false)
  const [reelDescription, setReelDescription] = useState('')
  const [useSummaryAsDescription, setUseSummaryAsDescription] = useState(false)
  // Default ±10s (Step 25g). Applied to KW/Mux/chapter segments. LLM
  // detector items skip the padding in the projection below.
  const [preMs, setPreMs] = useState(10000)
  const [postMs, setPostMs] = useState(10000)
  const [creating, setCreating] = useState(false)
  const [createErr, setCreateErr] = useState(null)
  const [createdReel, setCreatedReel] = useState(null)

  // Filters over the candidate list. All on by default = current behavior.
  // sourceFilter narrows by detector: 'mux_km' | 'tbtv_ai_keyword' |
  //   'tbtv_ai_llm' | 'mux_chapters' | 'tbtv_ai_chapters'.
  // typeFilter narrows by moment_type (goal, save, penalty, …). Applies to
  //   key_moments only — chapters don't carry a type.
  // keywordPatternFilter narrows specifically the TBTV-AI keyword sublayer
  //   to a chosen set of patterns (e.g. just 'goal' and 'fight'). Pattern
  //   strings are extracted from concepts[1] = 'keyword:<pattern>'.
  const [sourceFilter, setSourceFilter] = useState(() => new Set([
    'mux_km', 'tbtv_ai_keyword', 'tbtv_ai_llm', 'mux_chapters', 'tbtv_ai_chapters',
  ]))
  const [typeFilter, setTypeFilter] = useState(() => new Set())  // empty = all types
  const [keywordPatternFilter, setKeywordPatternFilter] = useState(() => new Set())  // empty = all patterns

  // Fetch all three Robots outputs in parallel.
  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.keyMoments(tbtv_id).catch(e => ({ moments: [], _err: e.message })),
      api.chapters(tbtv_id).catch(e => ({ chapters: [], _err: e.message })),
      api.summary(tbtv_id).catch(e => ({ summary: null, _err: e.message })),
    ]).then(([m, c, s]) => {
      if (cancelled) return
      // Step 25g: drop AI-keyword moments contained within an AI-LLM
      // moment's span — the same play surfacing twice as different rows
      // creates real friction in the composer. Editor can disable the
      // AI LLM source-filter chip to see the raw KW rows again.
      const dedupedMoments = dedupAiKeywordAgainstLlm(m.moments || [])
      setMoments(dedupedMoments)
      setChapters(c.chapters || [])
      setSummary(s.summary || null)
      if (m._err || c._err || s._err) setErr([m._err, c._err, s._err].filter(Boolean).join(' · '))
      // Default: select all key moments. Chapters unchecked by default.
      const initial = new Set(dedupedMoments.map(km => `key_moment:${km.start_ms}`))
      setSelected(initial)
    })
    return () => { cancelled = true }
  }, [tbtv_id])

  // When the user ticks "Use summary as description" and the summary loads,
  // populate the description field (replacing whatever was there).
  useEffect(() => {
    if (useSummaryAsDescription && summary?.description) {
      setReelDescription(summary.description)
    }
  }, [useSummaryAsDescription, summary])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Merge key moments + chapters into one chronological list.
  //
  // For chapters: Mux's Generate Chapters returns coarse-grained section
  // markers (e.g. "First Period", "Halftime Commercials") that start at the
  // section boundary — often before the action. A reel anchored on
  // chapter.start_ms would lead with intro time. We anchor each chapter on
  // the FIRST KEY MOMENT inside the chapter's range instead (chapter[i].start
  // ≤ km.start < chapter[i+1].start). If no key moments fall inside, we
  // fall back to chapter.start_ms.
  //
  // `effective_start_ms` is what the modal displays AND what gets passed to
  // /api/highlights/reels as the segment timestamp.
  // Map a key_moment row to one of the 3 detector buckets used by the source
  // filter. detector is null for Mux moments, 'keyword' / 'llm' for TBTV-AI.
  const kmDetectorKey = (m) => {
    if (m.source === 'mux') return 'mux_km'
    if (m.source === 'tbtv_ai' && m.detector === 'keyword') return 'tbtv_ai_keyword'
    if (m.source === 'tbtv_ai' && m.detector === 'llm') return 'tbtv_ai_llm'
    return 'mux_km'  // safe default for legacy rows
  }
  const chDetectorKey = (c) => c.source === 'tbtv_ai' ? 'tbtv_ai_chapters' : 'mux_chapters'

  // Per-row badge labels + colors, aligned 1:1 with the source-filter chips
  // above. Lets editors tell at a glance whether a row came from Mux's
  // detector, the TBTV-AI keyword sublayer, or the TBTV-AI LLM sublayer
  // even when the source-filter is set to show ALL buckets.
  // AI KW and AI LLM both produce Key Moments — keep "Key Moment" in the
  // label so editors don't read "AI KW" as a separate object type.
  const BUCKET_BADGE = {
    mux_km:           { label: 'Mux Key Moment',          color: '#fb923c' },
    tbtv_ai_keyword:  { label: 'AI Keyword Key Moment',   color: '#a78bfa' },
    tbtv_ai_llm:      { label: 'AI LLM Key Moment',       color: '#22c55e' },
    mux_chapters:     { label: 'Mux Chapter',             color: '#fb923c' },
    tbtv_ai_chapters: { label: 'AI Chapter',              color: '#22c55e' },
  }
  const bucketBadge = (it) => {
    const k = it._type === 'chapter' ? chDetectorKey(it) : kmDetectorKey(it)
    return BUCKET_BADGE[k] || { label: it._type === 'chapter' ? 'CHAPTER' : 'KEY MOMENT', color: '#888' }
  }

  // For keyword-sublayer moments, the matched pattern is encoded as the
  // second concepts entry: ['<type>', 'keyword:<pattern>']. Extract it so
  // the per-keyword filter chips can render.
  const extractKeywordPattern = (m) => {
    const concepts = Array.isArray(m.concepts) ? m.concepts : []
    const tagged = concepts.find(c => typeof c === 'string' && c.startsWith('keyword:'))
    return tagged ? tagged.slice('keyword:'.length) : null
  }

  // Build the full candidate set BEFORE filtering — used both to drive the
  // filter chip counts (so editors can see how big each bucket is) and to
  // produce the rendered list after filtering.
  const allItems = useMemo(() => {
    const sortedMoments = [...(moments || [])].sort((a, b) => a.start_ms - b.start_ms)
    const sortedChapters = [...(chapters || [])].sort((a, b) => a.start_ms - b.start_ms)

    const ch = sortedChapters.map((c, idx) => {
      const next = sortedChapters[idx + 1]
      const chStart = c.start_ms
      const chEnd = next ? next.start_ms : Number.MAX_SAFE_INTEGER
      const firstInside = sortedMoments.find(m => m.start_ms >= chStart && m.start_ms < chEnd)
      const effStart = firstInside ? firstInside.start_ms : chStart
      // Chapter window runs from the anchor to the next chapter boundary,
      // capped at CHAPTER_MAX_WINDOW_MS. Always at least 1s so the segment
      // isn't empty if the next chapter starts immediately.
      const effEnd = Math.max(effStart + 1000, Math.min(chEnd, effStart + CHAPTER_MAX_WINDOW_MS))
      return {
        ...c,
        _type: 'chapter',
        _anchored_to_moment: !!firstInside,
        // Display + reel anchor: first key moment in range, else chapter start.
        effective_start_ms: effStart,
        effective_end_ms: effEnd,
      }
    })

    const km = sortedMoments.map(m => ({
      ...m,
      _type: 'key_moment',
      effective_start_ms: m.start_ms,
    }))
    return [...km, ...ch].sort((a, b) => a.effective_start_ms - b.effective_start_ms)
  }, [moments, chapters])

  // Counts for the source-filter chip badges. Computed against the
  // UNFILTERED set so toggling a chip off still shows its real bucket size.
  const sourceCounts = useMemo(() => {
    const c = { mux_km: 0, tbtv_ai_keyword: 0, tbtv_ai_llm: 0, mux_chapters: 0, tbtv_ai_chapters: 0 }
    for (const it of allItems) {
      if (it._type === 'key_moment') c[kmDetectorKey(it)]++
      else if (it._type === 'chapter') c[chDetectorKey(it)]++
    }
    return c
  }, [allItems])

  // Distinct moment_types across all key moments — drives the type-chip row.
  // Counted only on key_moment rows; chapters have no moment_type.
  const typeCounts = useMemo(() => {
    const c = {}
    for (const it of allItems) {
      if (it._type !== 'key_moment') continue
      const t = it.moment_type || '(other)'
      c[t] = (c[t] || 0) + 1
    }
    return c
  }, [allItems])

  // Distinct keyword patterns and their counts. Only relevant for the
  // TBTV-AI keyword sublayer; sourced from concepts[1].
  const keywordPatternCounts = useMemo(() => {
    const c = {}
    for (const it of allItems) {
      if (it._type !== 'key_moment') continue
      if (kmDetectorKey(it) !== 'tbtv_ai_keyword') continue
      const p = extractKeywordPattern(it)
      if (!p) continue
      c[p] = (c[p] || 0) + 1
    }
    return c
  }, [allItems])

  // Apply all three filters.
  const items = useMemo(() => {
    return allItems.filter(it => {
      const detKey = it._type === 'chapter' ? chDetectorKey(it) : kmDetectorKey(it)
      if (!sourceFilter.has(detKey)) return false
      // Type filter applies to key_moments only. Empty = all types.
      if (it._type === 'key_moment' && typeFilter.size > 0) {
        if (!typeFilter.has(it.moment_type || '(other)')) return false
      }
      // Keyword-pattern filter applies ONLY when this is a keyword-sublayer
      // row AND at least one pattern chip is active. Empty = include all.
      if (detKey === 'tbtv_ai_keyword' && keywordPatternFilter.size > 0) {
        const p = extractKeywordPattern(it)
        if (!p || !keywordPatternFilter.has(p)) return false
      }
      return true
    })
  }, [allItems, sourceFilter, typeFilter, keywordPatternFilter])

  const loading = moments == null || chapters == null
  const selectedCount = selected.size
  const selectedMoments = items.filter(it => selected.has(itemKey(it)) && it._type === 'key_moment').length
  const selectedChapters = items.filter(it => selected.has(itemKey(it)) && it._type === 'chapter').length

  // Smart default title — composed from the selection. Only auto-updates
  // while the editor hasn't manually edited the title (titleEdited === false).
  useEffect(() => {
    if (titleEdited) return
    const base = title || tbtv_id
    if (selectedCount === 0) {
      setReelTitle(`${base} — highlights`)
      return
    }
    const parts = []
    if (selectedMoments > 0) parts.push(`${selectedMoments} key moment${selectedMoments === 1 ? '' : 's'}`)
    if (selectedChapters > 0) parts.push(`${selectedChapters} chapter${selectedChapters === 1 ? '' : 's'}`)
    setReelTitle(`${base} — ${parts.join(' + ')}`)
  }, [titleEdited, selectedCount, selectedMoments, selectedChapters, title, tbtv_id])

  function toggle(it) {
    setSelected(prev => {
      const next = new Set(prev)
      const k = itemKey(it)
      if (next.has(k)) next.delete(k); else next.add(k)
      return next
    })
  }

  function selectAll(type) {
    setSelected(prev => {
      const next = new Set(prev)
      for (const it of items) {
        if (!type || it._type === type) next.add(itemKey(it))
      }
      return next
    })
  }
  function clearSelection() { setSelected(new Set()) }

  async function create() {
    if (selectedCount === 0) return
    setCreating(true)
    setCreateErr(null)
    try {
      // Chapter selections carry an explicit window so they don't collapse
      // into adjacent key-moment ±pre/post windows during the backend merge.
      // Key-moment selections now ALSO pass an explicit window using the
      // moment's actual end_ms (Step 25g): pre-fix, the bare {start_ms}
      // payload fell back to the backend's start_ms + post_ms calc, which
      // produced uniform 20s clips regardless of how wide the underlying
      // moment was. Now wide goals (e.g. Sullivan 54s) get a proper full
      // window in the resulting reel segment.
      const segments = items
        .filter(it => selected.has(itemKey(it)))
        .map(it => {
          if (it._type === 'chapter') {
            return {
              tbtv_id,
              start_ms: it.effective_start_ms,
              window_start_ms: it.effective_start_ms,
              window_end_ms: it.effective_end_ms,
            }
          }
          // key_moment — LLM-detector moments use their bare span (the LLM
          // already chose the right lead-up + tail). Other detectors
          // (keyword, Mux, manual) get ±preMs/postMs padding.
          const isLlm = it.detector === 'llm'
          const segPre = isLlm ? 0 : preMs
          const segPost = isLlm ? 0 : postMs
          return {
            tbtv_id,
            start_ms: it.start_ms,
            end_ms: it.end_ms,
            window_start_ms: Math.max(0, it.start_ms - segPre),
            window_end_ms: (it.end_ms || it.start_ms) + segPost,
          }
        })
      const r = await api.createReel({
        title: reelTitle.trim() || undefined,
        description: reelDescription.trim() || undefined,
        query: 'single-video composer',
        segments,
        pre_ms: preMs,
        post_ms: postMs,
        // Origin records that this reel was composed from a single video.
        // Editor's Reel Pool tab already covers per-video restoration via
        // segments' source_tbtv_id, so this is metadata for future use.
        origin: { kind: 'video_composer', tbtv_id },
      })
      setCreatedReel(r)
      onCreated?.(r)
    } catch (e) {
      setCreateErr(e.message)
    } finally {
      setCreating(false)
    }
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div style={{
        background: '#111', border: '1px solid #2a2a2a', borderRadius: 8,
        width: 'min(820px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Film size={16} /> Build reel from this video
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title || tbtv_id}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Selection summary + bulk select */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
          <strong>{selectedCount}</strong> selected
          <span style={{ color: '#888' }}>
            ({selectedMoments} 💫 · {selectedChapters} 📑)
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={() => selectAll('key_moment')} disabled={loading}
            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 11 }}>
            All key moments
          </button>
          <button onClick={() => selectAll('chapter')} disabled={loading}
            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 11 }}>
            All chapters
          </button>
          <button onClick={() => selectAll()} disabled={loading}
            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 11 }}>
            Select all
          </button>
          <button onClick={clearSelection} disabled={loading || selectedCount === 0}
            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 11 }}>
            Clear
          </button>
        </div>

        {/* Filter row 1 — source / detector (Mux KM, AI keyword, AI LLM,
            Mux chapters, AI chapters). Each chip toggles inclusion of that
            detector's rows. Count is the unfiltered bucket size. */}
        {!loading && allItems.length > 0 && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#888', minWidth: 50 }}>Source:</span>
              <SourceChip k="mux_km"            label="Mux KM"        count={sourceCounts.mux_km}            color="#fb923c" set={sourceFilter} setSet={setSourceFilter} />
              <SourceChip k="tbtv_ai_keyword"   label="AI keyword"    count={sourceCounts.tbtv_ai_keyword}   color="#a78bfa" set={sourceFilter} setSet={setSourceFilter} />
              <SourceChip k="tbtv_ai_llm"       label="AI LLM"        count={sourceCounts.tbtv_ai_llm}       color="#22c55e" set={sourceFilter} setSet={setSourceFilter} />
              <SourceChip k="mux_chapters"      label="Mux chapters"  count={sourceCounts.mux_chapters}      color="#fb923c" set={sourceFilter} setSet={setSourceFilter} />
              <SourceChip k="tbtv_ai_chapters"  label="AI chapters"   count={sourceCounts.tbtv_ai_chapters}  color="#22c55e" set={sourceFilter} setSet={setSourceFilter} />
              <div style={{ flex: 1 }} />
              {(sourceFilter.size < 5) && (
                <button onClick={() => setSourceFilter(new Set(['mux_km','tbtv_ai_keyword','tbtv_ai_llm','mux_chapters','tbtv_ai_chapters']))}
                  style={{ fontSize: 10, color: '#7dd3fc', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                  Reset
                </button>
              )}
            </div>

            {/* Filter row 2 — moment_type chips (goal / save / penalty / …)
                Apply to key_moments only. Empty selection = all types. */}
            {Object.keys(typeCounts).length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#888', minWidth: 50 }}>Type:</span>
                {Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
                  <TypeChip key={t} k={t} label={t.replace('_', ' ')} count={n} set={typeFilter} setSet={setTypeFilter} />
                ))}
                {typeFilter.size > 0 && (
                  <button onClick={() => setTypeFilter(new Set())}
                    style={{ fontSize: 10, color: '#7dd3fc', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    Clear types
                  </button>
                )}
              </div>
            )}

            {/* Filter row 3 — keyword pattern chips. Only relevant + shown
                when at least one keyword-sublayer row exists in the data
                AND the AI keyword source chip is enabled (else there's
                nothing to narrow further). */}
            {sourceFilter.has('tbtv_ai_keyword') && Object.keys(keywordPatternCounts).length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#888', minWidth: 50, marginTop: 2 }}>Keyword:</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
                  {Object.entries(keywordPatternCounts).sort((a, b) => b[1] - a[1]).map(([p, n]) => (
                    <TypeChip key={p} k={p} label={p} count={n} set={keywordPatternFilter} setSet={setKeywordPatternFilter} />
                  ))}
                </div>
                {keywordPatternFilter.size > 0 && (
                  <button onClick={() => setKeywordPatternFilter(new Set())}
                    style={{ fontSize: 10, color: '#7dd3fc', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    Clear keywords
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {summary && (summary.title || summary.description || summary.tags) && (
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid #1f1f1f',
              background: 'rgba(167,139,250,0.05)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <FileText size={12} style={{ color: '#a78bfa' }} />
                <span style={{ fontSize: 11, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Mux summary
                </span>
              </div>
              {summary.title && (
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  {summary.title}
                </div>
              )}
              {summary.description && (
                <div style={{ fontSize: 12, color: '#bbb', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                  {summary.description}
                </div>
              )}
              {Array.isArray(summary.tags) && summary.tags.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {summary.tags.map((t, i) => (
                    <span key={i} style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 10,
                      background: 'rgba(167,139,250,0.15)', color: '#c4b5fd',
                    }}>{t}</span>
                  ))}
                </div>
              )}
            </div>
          )}
          {err && <div style={{ padding: 12, color: 'crimson', fontSize: 12 }}>{err}</div>}
          {loading && (
            <div style={{ padding: 24, color: '#888', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Loader2 size={14} className="spin" /> Loading moments and chapters…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div style={{ padding: 24, color: '#888', textAlign: 'center', fontSize: 13 }}>
              No key moments or chapters for this video yet. Apply Robots first
              (Caption Library row → Apply Robots in Key Moments tab → Find Key
              Moments + Generate Chapters).
            </div>
          )}
          {!loading && items.length > 0 && (
            <div>
              {items.map((it) => {
                const k = itemKey(it)
                const isSelected = selected.has(k)
                const isMoment = it._type === 'key_moment'
                return (
                  <label
                    key={k}
                    style={{
                      display: 'flex', gap: 10, padding: '8px 16px',
                      borderBottom: '1px solid #1a1a1a',
                      background: isSelected ? 'rgba(59,130,246,0.06)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(it)}
                      style={{ marginTop: 4 }}
                    />
                    <div style={{
                      width: 60, flexShrink: 0, color: '#666',
                      fontFamily: 'monospace', fontSize: 12,
                      fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                    }}>
                      {fmtTime(it.effective_start_ms ?? it.start_ms)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        {(() => {
                          const b = bucketBadge(it)
                          return (
                            <>
                              {isMoment
                                ? <Sparkles size={12} style={{ color: b.color }} />
                                : <BookOpen size={12} style={{ color: b.color }} />}
                              <span style={{
                                fontSize: 11, color: b.color,
                                background: `${b.color}22`,
                                padding: '1px 6px', borderRadius: 3,
                                fontWeight: 600,
                              }}>
                                {b.label}
                              </span>
                            </>
                          )
                        })()}
                        {isMoment && it.quality_score != null && (
                          <span style={{ fontSize: 11, color: scoreColor(it.quality_score), fontFamily: 'monospace' }}>
                            · {it.quality_score.toFixed(2)}
                          </span>
                        )}
                        {!isMoment && it._anchored_to_moment && (
                          <span style={{ fontSize: 10, color: '#888', fontStyle: 'italic' }}
                            title="Chapter anchored on the first key moment within its section">
                            · anchored to first 💫
                          </span>
                        )}
                        {!isMoment && it.effective_end_ms != null && (
                          <span style={{ fontSize: 10, color: '#888', fontFamily: 'monospace' }}
                            title={`Chapter clip spans ~${Math.round((it.effective_end_ms - it.effective_start_ms) / 1000)}s`}>
                            · ~{Math.round((it.effective_end_ms - it.effective_start_ms) / 1000)}s
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {it.title || (isMoment ? '(untitled moment)' : '(untitled chapter)')}
                      </div>
                      {isMoment && it.audible_narrative && (
                        <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                          🔊 {it.audible_narrative}
                        </div>
                      )}
                    </div>
                  </label>
                )
              })}
            </div>
          )}
        </div>

        {createdReel?.playback_url && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid #1f1f1f', background: 'rgba(34,197,94,0.06)' }}>
            <div style={{ fontSize: 12, color: '#22c55e', marginBottom: 6, fontWeight: 500 }}>
              ✓ Reel ready · share URL:
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                readOnly
                value={createdReel.playback_url}
                onClick={(e) => e.currentTarget.select()}
                style={{ flex: 1, padding: '6px 8px', fontSize: 12, fontFamily: 'monospace',
                  background: '#0d1622', border: '1px solid #2a2a2a', borderRadius: 4, color: '#bfdbfe' }}
              />
              <button
                onClick={() => navigator.clipboard?.writeText(createdReel.playback_url)}
                style={{ padding: '6px 12px', fontSize: 12, background: '#3b82f6', color: 'white',
                  border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                Copy
              </button>
              <button
                onClick={onClose}
                style={{ padding: '6px 12px', fontSize: 12, background: 'transparent', color: 'inherit',
                  border: '1px solid #333', borderRadius: 4, cursor: 'pointer' }}>
                Done
              </button>
            </div>
          </div>
        )}

        {/* Footer: settings + create */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid #1f1f1f', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Reel title"
              value={reelTitle}
              onChange={(e) => { setReelTitle(e.target.value); setTitleEdited(true) }}
              style={{ flex: 1, minWidth: 240, padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', fontSize: 13 }}
            />
            <label style={{ fontSize: 11, color: '#888', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input
                type="checkbox"
                checked={useSummaryAsDescription}
                onChange={(e) => {
                  const next = e.target.checked
                  setUseSummaryAsDescription(next)
                  if (!next) setReelDescription('')
                }}
                disabled={!summary?.description}
              />
              <span title={summary?.description ? '' : 'Run Summarize Robot on this video first'}>
                Use summary as description
              </span>
            </label>
            <label style={{ fontSize: 11, color: '#888', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              pre (s):
              <input type="number" min={0} max={60} value={preMs / 1000}
                onChange={(e) => setPreMs(Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)) * 1000)}
                style={{ width: 50, padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }} />
            </label>
            <label style={{ fontSize: 11, color: '#888', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              post (s):
              <input type="number" min={0} max={60} value={postMs / 1000}
                onChange={(e) => setPostMs(Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)) * 1000)}
                style={{ width: 50, padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }} />
            </label>
          </div>
          <textarea
            placeholder="Reel description (optional — populated from summary if checked)"
            value={reelDescription}
            onChange={(e) => setReelDescription(e.target.value)}
            rows={reelDescription ? 3 : 1}
            style={{
              width: '100%', padding: '6px 10px', background: 'transparent',
              border: '1px solid #333', borderRadius: 4, color: 'inherit',
              fontSize: 12, fontFamily: 'inherit', resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }} />
            {createErr && <span style={{ color: 'crimson', fontSize: 12 }}>{createErr}</span>}
            <button
              onClick={create}
              disabled={selectedCount === 0 || creating || !!createdReel}
              style={{
                padding: '8px 16px',
                background: selectedCount === 0 || createdReel ? '#222' : '#3b82f6',
                border: 'none', borderRadius: 4, color: 'white',
                cursor: selectedCount === 0 || createdReel ? 'default' : 'pointer',
                fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              {creating ? <Loader2 size={13} className="spin" /> : <Film size={13} />}
              {createdReel ? 'Reel created' : `Create reel (${selectedCount})`}
            </button>
          </div>
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
      </div>
    </div>
  )
}

// Colored toggle chip for the Source filter row. Active = colored; inactive
// = muted. Click toggles inclusion of this detector. Count is rendered in
// parens.
function SourceChip({ k, label, count, color, set, setSet }) {
  const active = set.has(k)
  const onToggle = () => {
    setSet(prev => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k); else next.add(k)
      return next
    })
  }
  if (count === 0) {
    // Show as disabled — no rows means nothing to filter on, but keep it
    // visible so the editor can see why a detector chip is greyed out.
    return (
      <span style={{
        fontSize: 11, padding: '2px 8px', borderRadius: 10,
        background: 'rgba(255,255,255,0.03)', color: '#555',
        border: '1px solid #222', cursor: 'default',
      }}>
        {label} (0)
      </span>
    )
  }
  return (
    <button onClick={onToggle}
      style={{
        fontSize: 11, padding: '2px 8px', borderRadius: 10,
        background: active ? `${color}22` : 'rgba(255,255,255,0.04)',
        color: active ? color : '#888',
        border: `1px solid ${active ? color + '55' : '#333'}`,
        cursor: 'pointer', fontWeight: 600, letterSpacing: 0.2,
      }}>
      {label} ({count})
    </button>
  )
}

// Multiselect toggle chip used for Type + Keyword filter rows. Default state
// is "all" — clicking a chip moves to "only these chips" selection mode.
// Empty selection set = include all.
function TypeChip({ k, label, count, set, setSet }) {
  const active = set.has(k)
  const onToggle = () => {
    setSet(prev => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k); else next.add(k)
      return next
    })
  }
  return (
    <button onClick={onToggle}
      style={{
        fontSize: 11, padding: '1px 7px', borderRadius: 10,
        background: active ? 'rgba(125,211,252,0.15)' : 'rgba(255,255,255,0.04)',
        color: active ? '#7dd3fc' : '#bbb',
        border: `1px solid ${active ? '#7dd3fc55' : '#333'}`,
        cursor: 'pointer',
      }}>
      {label} ({count})
    </button>
  )
}
